import { useEffect } from 'react';

/**
 * 1 ms of silence. Used only to "bless" an <audio> element on the first user
 * gesture — see {@link useAudioUnlock}. Never audible.
 */
export const SILENT_WAV =
  'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';

/** Gestures that count as user activation for the unlock below. */
export const UNLOCK_EVENTS = ['pointerdown', 'touchend', 'keydown'] as const;

/**
 * Mobile Safari (and Chrome's autoplay policy) will only let an <audio> element
 * play programmatically once it has played at least once inside a user gesture.
 * Playback that starts from a promise continuation (an IndexedDB read) is NOT a
 * gesture — so without this the very first clip of a session silently refuses
 * to play and the user has to hunt for the Replay button.
 *
 * So: on the first gesture anywhere in the document, play 1 ms of silence on
 * the element and immediately stop. That marks the element as user-activated
 * for the rest of its life, and every later programmatic play works.
 *
 * `unlockedRef` / `unlockingRef` are owned by the caller because the caller's
 * own `play()` has to be able to say "I am real playback, don't stop me" — see
 * the comment on `settle` below.
 *
 * Every element that plays audio needs its own blessing, so both the clip
 * player and the context player mount this hook against their own element.
 */
export function useAudioUnlock(
  audioRef: React.RefObject<HTMLAudioElement | null>,
  unlockedRef: React.MutableRefObject<boolean>,
  unlockingRef: React.MutableRefObject<boolean>,
): void {
  useEffect(() => {
    if (unlockedRef.current) return;

    const detach = () => {
      for (const type of UNLOCK_EVENTS) document.removeEventListener(type, unlock, true);
    };

    function unlock() {
      const node = audioRef.current;
      if (!node || unlockedRef.current) return;
      unlockedRef.current = true;
      detach();

      // already playing => the element is blessed already, leave it alone
      if (!node.paused) return;

      const hadSrc = Boolean(node.getAttribute('src'));
      if (!hadSrc) node.src = SILENT_WAV;
      node.muted = true;
      unlockingRef.current = true;

      /**
       * `stop` is decided at settle time, not now: the gesture that triggers the
       * unlock is usually the Play tap itself, and this listener runs (capture
       * phase) BEFORE React's click handler. If that handler started real
       * playback while `play()` was still resolving, pausing here would kill the
       * clip the user just asked for — `play()` clears the flag for exactly that
       * case.
       */
      const settle = (stop: boolean) => {
        unlockingRef.current = false;
        node.muted = false;
        if (stop) {
          node.pause();
          try {
            node.currentTime = 0;
          } catch {
            // currentTime before metadata throws on some Safari builds
          }
        }
        // never leave the placeholder behind: `play()` treats "no src" as
        // "nothing to play", and playing 1 ms of silence would be a no-op
        if (node.getAttribute('src') === SILENT_WAV) {
          node.removeAttribute('src');
          node.load();
        }
      };

      try {
        const started = node.play() as Promise<void> | undefined;
        if (started && typeof started.then === 'function') {
          started.then(
            () => settle(unlockingRef.current),
            () => {
              settle(false);
              unlockedRef.current = false; // let a later gesture try again
            },
          );
        } else {
          settle(true);
        }
      } catch {
        settle(false);
        unlockedRef.current = false;
      }
    }

    for (const type of UNLOCK_EVENTS) document.addEventListener(type, unlock, true);
    return detach;
    // refs are stable; this must run exactly once per element
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Seek without letting the seek swallow whatever comes next: Safari throws
 * InvalidStateError when `currentTime` is set before metadata has loaded.
 */
export function seekSafely(el: HTMLAudioElement, seconds: number): void {
  try {
    el.currentTime = seconds;
  } catch {
    /* pre-metadata seek — the element will start from wherever it can */
  }
}
